import type { DocumentAnalysisResult } from '../api/analysis';

type ResultSummaryProps = {
  filename: string;
  result: DocumentAnalysisResult;
};

function confidenceBand(confidence: number) {
  if (confidence < 0.35) {
    return {
      badge: 'bg-emerald-100 text-emerald-900',
      gradient: 'from-emerald-500 to-teal-600',
      label: 'Low AI probability',
    };
  }
  if (confidence < 0.7) {
    return {
      badge: 'bg-amber-100 text-amber-900',
      gradient: 'from-amber-400 to-orange-500',
      label: 'Mixed or uncertain',
    };
  }
  return {
    badge: 'bg-rose-100 text-rose-900',
    gradient: 'from-rose-500 to-red-600',
    label: 'High AI probability',
  };
}

export function ResultSummary({ filename, result }: ResultSummaryProps) {
  const band = confidenceBand(result.confidence);

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-700">Analysis Summary</p>
          <h2 className="mt-2 text-3xl font-semibold text-slate-950">{filename}</h2>
          <p className="mt-1 text-base text-slate-600">
            Model family fingerprint: <span className="font-semibold text-slate-900">{result.model_family}</span>
          </p>
        </div>
        <div className={`rounded-full px-4 py-2 text-sm font-semibold ${band.badge}`}>{band.label}</div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)]">
        <div className="rounded-[1.75rem] bg-slate-950 p-6 text-white">
          <p className="text-sm uppercase tracking-[0.25em] text-slate-300">Overall AI probability</p>
          <div className="mt-4 flex items-end gap-4">
            <span className="text-6xl font-semibold">{Math.round(result.confidence * 100)}%</span>
            <div className="mb-2 h-3 flex-1 overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${band.gradient}`}
                style={{ width: `${Math.max(6, Math.round(result.confidence * 100))}%` }}
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div className="rounded-[1.75rem] bg-slate-100 p-5">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-500">Word count</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{result.word_count}</p>
          </div>
          <div className="rounded-[1.75rem] bg-slate-100 p-5">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-500">Sentence count</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{result.sentence_count}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
